import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getAttention } from '../../api/client'
import { Icon } from '../../design/Icon'
import { usePermissions } from '../../shell/PermissionsContext'

/**
 * "2 need attention" in the header (Part B2, U9): paused rollouts, rollouts waiting for a person,
 * failures, settings changed on devices, drafts left unpublished, and provisioning (devices waiting
 * for approval or quarantined, claims misbehaving), and devices connected without encryption.
 * Nothing is shown when nothing needs anyone — a permanent "0" is noise people learn to ignore.
 */
export function AttentionBadge() {
  const { hasPermission } = usePermissions()
  // Provisioning's items (devices waiting for approval, quarantines, claims in trouble) are shown
  // to anyone who can see provisioning, even with no rollouts or settings to look after; devices
  // connected without encryption, to anyone who can see devices.
  const allowed =
    hasPermission('deployment.view') ||
    hasPermission('policy.view') ||
    hasPermission('provisioning.view') ||
    hasPermission('device.view')
  const attention = useQuery({
    queryKey: ['attention'],
    queryFn: getAttention,
    enabled: allowed,
    retry: false,
    refetchInterval: 60_000,
  })
  const count = attention.data?.count ?? 0
  if (!allowed || count === 0) return null
  return (
    <Link
      to="/"
      className="flex items-center gap-xs rounded-full bg-warning-bg px-sm py-xs text-xs font-medium text-warning"
      aria-label={`${count} ${count === 1 ? 'thing needs' : 'things need'} attention`}
    >
      <Icon name="alert-triangle" size="xs" />
      {/* The number alone on a phone; the link's label says the rest. */}
      {count}{' '}
      <span className="hidden sm:inline">need{count === 1 ? 's' : ''} attention</span>
    </Link>
  )
}
